// How the support chats print times, in the staff member's time zone
// (state.timeZone): Arabic words with Latin digits, like the rest of the
// dashboard. Intl formatters are costly to build, so each one is made once.
const LOCALE = "ar-u-nu-latn";
const formatters = new Map();

const formatter = (locale, timeZone, options) => {
  const key = `${locale}|${timeZone || ""}|${JSON.stringify(options)}`;
  let value = formatters.get(key);
  if (!value) {
    value = new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: timeZone || undefined,
    });
    formatters.set(key, value);
  }
  return value;
};

// "2026-10-21": the calendar day a moment falls on in that zone.
export const dayKey = (date, timeZone) =>
  formatter("en-CA", timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

const dayNumber = (key) => {
  const [year, month, day] = key.split("-").map(Number);
  return Date.UTC(year, month - 1, day) / 86400000;
};

// Calendar days from `date` to `now` (0 = the same day).
const daysAgo = (date, timeZone, now) =>
  dayNumber(dayKey(now, timeZone)) - dayNumber(dayKey(date, timeZone));

const sameYear = (date, timeZone, now) =>
  dayKey(date, timeZone).slice(0, 4) === dayKey(now, timeZone).slice(0, 4);

// "5:06 م"
export const formatClock = (date, timeZone) =>
  formatter(LOCALE, timeZone, { hour: "numeric", minute: "2-digit" }).format(
    date,
  );

// The divider between days in a thread: اليوم · أمس · الاثنين 19 أكتوبر
// (with the year when it isn't this year).
export const formatDayDivider = (date, timeZone, now = new Date()) => {
  const ago = daysAgo(date, timeZone, now);
  if (ago === 0) return "اليوم";
  if (ago === 1) return "أمس";
  return formatter(LOCALE, timeZone, {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(sameYear(date, timeZone, now) ? {} : { year: "numeric" }),
  }).format(date);
};

// A chat's last activity in the list: the time today, أمس, the weekday within
// a week, else the date.
export const formatListStamp = (date, timeZone, now = new Date()) => {
  const ago = daysAgo(date, timeZone, now);
  if (ago <= 0) return formatClock(date, timeZone);
  if (ago === 1) return "أمس";
  if (ago < 7) return formatter(LOCALE, timeZone, { weekday: "long" }).format(date);
  return formatter(LOCALE, timeZone, {
    day: "numeric",
    month: "numeric",
    ...(sameYear(date, timeZone, now) ? {} : { year: "numeric" }),
  }).format(date);
};

// The full moment, for tooltips.
export const formatFullStamp = (date, timeZone) =>
  formatter(LOCALE, timeZone, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
